import React, { FC, useEffect, useState } from "react";
import { useSelector } from 'react-redux';
import TableTab from "../components/tabs/table-tab";
import { USER_LOGINS } from "../constants/menu-types";
import { useLocale } from "../contexts/localeContext";
import { TableContextProvider } from "../contexts/table/TableContextProvider";
import { TranslationKeys } from "../contexts/TranslationKeys";
import { Category } from "../models/Category";
import columns from '../models/ColumnDefinitions/NotificationColumns';
import NotificationSearch from "../models/search/NotificationSearch";
import { Lookup } from "../models/Lookup";
import { DeleteEntityResult } from "../models/services/DeleteEntityResult";
import { getNotifications, deleteNotification, getVRuleWithPagination } from "../services/notificationService";
import { getEmailAccountNamesWithPagination } from "../services/emailAccountService";
import EmailAccountSearch from "../models/search/EmailAccountsSearch";
import { ListQuery } from "../models/ListQuery";
import { appendFilter } from "../utilities/filters";
import { getActiveInactiveTableLegendItems } from "../models/TableLegend";
import { Filter } from "@/models/Filter";
import TableContextStateMonitor from "@/contexts/table/TableContextStateMonitor";

interface NotificationsTabProps {
    setBusy: (isBusy: boolean) => void,
    categories: Category[],
    setCategories: (category: Category[]) => void,
    onFilterChange: (filters: Filter[]) => void,
    setShowModal: (showModal: boolean) => void;
}

const NotificationsTab: FC<NotificationsTabProps> = ({ setBusy, categories, setCategories, onFilterChange, setShowModal }) => {
    const { translate } = useLocale();
    const title = translate.byKey(TranslationKeys.notifications);
    const [lookup, setLookup] = useState<Lookup[]>([]);
    const itemPath = '/access-control/notifications/notification/';
    
    useEffect(() => {
        setLookup([{
            name: 'vruleDisplayType.id',
            values: [],
            dataMethod: (listParameter?: ListQuery) => getVRuleWithPagination({
                ...listParameter, 
                filters: appendFilter("1,2", "Notification Type", "id", listParameter?.filters ?? [], "=in=")
            }), // Hard-coding the filter to Schedule and Event-based notification since these are the only supported for now.  
            searchAccessor: NotificationSearch.find(item => item.accessor === "vruleDisplayType")?.searchOnName
        },  
        {
            name: "accountId",
            values: [],
            dataMethod: getEmailAccountNamesWithPagination,
            searchAccessor: EmailAccountSearch.find(item => item.accessor === "name")?.searchOnName
        }
    ]);
    }, [])

    const onDelete = (id: string): Promise<DeleteEntityResult> => {
        setBusy(true);
        return new Promise((resolve, reject) => {
            deleteNotification(id)
                .then(
                    deleteResult => {
                        resolve(deleteResult);
                        setCategories([{ ...categories[0], count: (categories[0]?.count ?? 1) - 1 }]);
                        setBusy(false);
                    },
                    deleteResult => {
                        reject(deleteResult);
                        setBusy(false);
                    })
        });
    }

    const userLoginId = useSelector((state: any) => (state.userInfo.id))
    const currentMenuDetail = USER_LOGINS;
    const initialiseUserPreferences = (setDetails: (userLoginId, currentMenuDetail) => void) => {
        setDetails(userLoginId, currentMenuDetail);
    }

    return <TableContextProvider
        initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
        dataMethod={listparameters => getNotifications(translate, listparameters)}
        tableDescriptor={columns}
        searchDescriptor={NotificationSearch}
        legendItems={getActiveInactiveTableLegendItems(translate)}
        useTable
        usePagination
        useSearch
        useSort
        useFilter
        deleteMethod={onDelete}
        lookup={lookup}
        onAddNew={() => setShowModal(true)}>
        <TableContextStateMonitor onFilterChange={onFilterChange} />
        <TableTab
            title={title}
            selectRows={false}
            rowPath={itemPath} 
            cyName="notifications" />
    </TableContextProvider>
};

export default NotificationsTab;
